// Wayward Comet (skill 'comet', owner 2026-11-02 'Make them all': a guided orb that zig-zags through the crowd). Render-only:
// reads sim.comets (position, velocity, shard flag) and the 'comet' / 'cometHit' / 'cometEnd' events; decides nothing.
// Teal-mint look:
//  - the head: a crisp cel orb (white-hot core, mint body, deep teal rim) with a soft halo and a spinning glint
//  - the tail: a ribbon through the head's real recent path, so every hard turn shows as a sharp corner in the trail; it
//    thins and fades toward the end, and lingers a moment after the comet is spent
//  - each hit: a white-mint impact star, sparks thrown off along the NEW heading (the turn reads), a small ground flash
//  - Splitstar: a bright burst and three short mint bolts toward the shards' first targets
import * as THREE from 'three/webgpu';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import type { GroundGlow } from './groundGlow';
import type { Lightning } from './lightning';
import { Cards, Ribbons, clamp01, col, glintLook, haloLook, orbLook, pushRolled } from './skillsBShared';

const CORE = 0xf2fffb, BODY = 0x3ff0c8, RIM = 0x078a7e, HALO = 0x40ffd0, TAIL = 0x00b49c, SPARK = 0x9affea;
const POOL = 24, PTS = 26, STEP = 0.2; // comets drawn at once; tail points per comet and their spacing along the path (m)
const Y = 1.6, Y_SHARD = 1.45;      // flight height above the ground (m): over the heads of the rank and file, so the zig-zag reads from above
const LINGER = 0.28;                // a spent comet's tail fades over this (s)

interface Trail { on: boolean; id: number; live: boolean; t: number; n: number; head: number; pts: Float32Array; shard: boolean; evo: boolean; y: number; hx: number; hy: number; hz: number }
const _e = new THREE.Color(), _c = new THREE.Color();

export class CometFx {
  private heads: Cards; private halos: Cards; private glints: Cards; private tails: Ribbons;
  private trails: Trail[] = Array.from({ length: POOL + 8 }, () => ({ on: false, id: 0, live: false, t: 0, n: 0, head: 0, pts: new Float32Array(PTS * 3), shard: false, evo: false, y: 0, hx: 0, hy: 0, hz: 0 }));
  private ordered = new Float32Array((PTS + 1) * 3);
  private hitN = 0;
  /** QA counters */
  thrown = 0; hits = 0; splits = 0;
  /** hero gesture: a throw to play (bumps per throw) and its yaw */
  throwSeq = 0; throwYaw = 0;

  constructor(scene: THREE.Scene, private fx: Fx, private bolts: Lightning, private glow: GroundGlow, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void) {
    this.tails = new Ribbons(scene, POOL + 8, PTS + 1, 5);
    this.halos = new Cards(scene, POOL, haloLook(HALO, 0.6), 4);
    this.heads = new Cards(scene, POOL, orbLook(CORE, BODY, RIM, 1.9), 6);
    this.glints = new Cards(scene, POOL, glintLook(0xc8fff2, 1.0), 7);
  }

  private trailOf(id: number): Trail | null { for (const t of this.trails) if (t.on && t.id === id) return t; return null; }

  event(ev: SimEvent, sim: Sim): void {
    const fx = this.fx;
    if (ev.t === 'comet') {
      const tr = this.trails.find((q) => !q.on) ?? this.trails.reduce((a, b) => (a.t > b.t ? a : b));
      const y = this.groundAt(ev.x, ev.z) + (ev.shard ? Y_SHARD : Y);
      tr.on = true; tr.id = ev.id; tr.live = true; tr.t = 0; tr.n = 0; tr.head = 0; tr.shard = ev.shard; tr.evo = ev.evolved; tr.y = y;
      if (!ev.shard) {
        this.thrown++; this.throwSeq++; this.throwYaw = Math.atan2(ev.dx, ev.dz);
        fx.stars.add(ev.x, y, ev.z, 1.5, 0.12, 0xe8fff8, 2.6);
        for (let k = 0; k < 6; k++) fx.particles.emitDir(ev.x, y, ev.z, ev.dx * (5 + Math.random() * 5) + (Math.random() - 0.5) * 3, Math.random() * 2, ev.dz * (5 + Math.random() * 5) + (Math.random() - 0.5) * 3, SPARK, 0.16, 0.22, 2.6);
      }
    } else if (ev.t === 'cometHit') {
      this.hits++;
      const big = !ev.shard, y = this.groundAt(ev.x, ev.z) + (big ? Y : Y_SHARD) + Math.max(0, ev.y - 1.6);
      fx.stars.add(ev.x, y, ev.z, big ? 1.9 : 1.2, 0.1, 0xffffff, 2.8);
      fx.stars.add(ev.x, y, ev.z, big ? 2.8 : 1.8, 0.18, BODY, 1.6);
      fx.rings.add(ev.x, this.groundAt(ev.x, ev.z) + 0.12, ev.z, big ? 1.8 : 1.1, 0.22, BODY, 1.6, 0.6);
      // the turn: a fan of sparks thrown along the new heading (or scattered when nothing is left to turn to)
      const hx = ev.dx || 0, hz = ev.dz || 0, has = hx !== 0 || hz !== 0;
      for (let k = 0; k < (big ? 9 : 5); k++) {
        const sp = 6 + Math.random() * 8, a = Math.random() * Math.PI * 2;
        const vx = has ? hx * sp + (Math.random() - 0.5) * 4 : Math.cos(a) * sp * 0.6, vz = has ? hz * sp + (Math.random() - 0.5) * 4 : Math.sin(a) * sp * 0.6;
        fx.particles.emitDir(ev.x, y, ev.z, vx, 1 + Math.random() * 3, vz, k % 3 ? SPARK : 0xffffff, big ? 0.2 : 0.14, 0.24, 3);
      }
      this.glow.flash(ev.x, ev.z, big ? 1.8 : 1.1, HALO, 0.55, 0.18);
      if ((this.hitN = (this.hitN + 1) % 3) === 0) this.burn(ev.x, ev.z, big ? 0.7 : 0.45);
      fx.shake = Math.min(1, fx.shake + (big ? 0.06 : 0.03));
    } else if (ev.t === 'cometEnd') {
      const tr = this.trailOf(ev.id); if (tr) { tr.live = false; tr.t = 0; }
      const y = tr ? tr.y : this.groundAt(ev.x, ev.z) + Y;
      if (ev.split) {
        this.splits++;
        fx.stars.add(ev.x, y, ev.z, 3.2, 0.2, 0xffffff, 2.6);
        fx.flashes.add(ev.x, y, ev.z, 0.9, 0.16, BODY, 1.2);
        fx.rings.add(ev.x, this.groundAt(ev.x, ev.z) + 0.12, ev.z, 3.2, 0.3, BODY, 2, 0.7);
        for (const c of sim.comets) if (c.alive && c.shard && Math.hypot(c.x - ev.x, c.z - ev.z) < 1.5) {
          const sp = Math.hypot(c.vx, c.vz) || 1, L = 2.4;
          this.bolts.add([ev.x, y, ev.z, ev.x + c.vx / sp * L, y + 0.2, ev.z + c.vz / sp * L], TAIL, 0.22, 0.16, 0.3);
        }
        fx.shake = Math.min(1, fx.shake + 0.12);
      } else for (let k = 0; k < 8; k++) { const a = Math.random() * Math.PI * 2, sp = 1.5 + Math.random() * 2; fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * sp, Math.random() * 1.5, Math.sin(a) * sp, SPARK, 0.18, 0.35, 2.2); }
    }
  }

  update(sim: Sim, dt: number, cam: THREE.Camera): void {
    const camQ = cam.quaternion, camP = cam.position, tm = performance.now() / 1000;
    this.heads.begin(); this.halos.begin(); this.glints.begin(); this.tails.begin();
    // live comets: record the path, draw the head
    for (const c of sim.comets) {
      if (!c.alive) continue;
      let tr = this.trailOf(c.id);
      if (!tr) { tr = this.trails.find((q) => !q.on) ?? null; if (!tr) continue; tr.on = true; tr.id = c.id; tr.live = true; tr.t = 0; tr.n = 0; tr.head = 0; tr.shard = c.shard; tr.evo = c.evo; tr.y = this.groundAt(c.x, c.z) + (c.shard ? Y_SHARD : Y); }
      const want = this.groundAt(c.x, c.z) + (c.shard ? Y_SHARD : Y);
      tr.y += (want - tr.y) * Math.min(1, dt * 10);
      // the path is sampled by distance (not per frame), so the tail keeps its length at any frame rate and shows each corner
      const last = ((tr.head - 1) % PTS + PTS) % PTS * 3;
      if (tr.n === 0 || Math.hypot(c.x - tr.pts[last], c.z - tr.pts[last + 2]) >= STEP) { const o = tr.head * 3; tr.pts[o] = c.x; tr.pts[o + 1] = tr.y; tr.pts[o + 2] = c.z; tr.head = (tr.head + 1) % PTS; tr.n = Math.min(PTS, tr.n + 1); }
      tr.hx = c.x; tr.hy = tr.y; tr.hz = c.z;
      const size = c.shard ? 0.8 : c.evo ? 1.5 : 1.3, pulse = 1 + Math.sin(tm * 22 + c.id) * 0.05;
      this.halos.push(c.x, tr.y, c.z, size * 3.2, camQ, 1);
      this.heads.push(c.x, tr.y, c.z, size * pulse, camQ, 1, 0.45);
      pushRolled(this.glints, c.x, tr.y, c.z, size * 2.3, camQ, 0.7, 0, tm * 6 + c.id);
      if (Math.random() < 0.6) this.fx.particles.emitDir(c.x + (Math.random() - 0.5) * 0.3, tr.y + (Math.random() - 0.5) * 0.3, c.z + (Math.random() - 0.5) * 0.3, -c.vx * 0.06, 0.4, -c.vz * 0.06, Math.random() < 0.5 ? SPARK : 0xffffff, c.shard ? 0.1 : 0.14, 0.3, 2.4);
    }
    // tails (live and lingering)
    for (const tr of this.trails) {
      if (!tr.on) continue;
      if (!tr.live) { tr.t += dt; if (tr.t >= LINGER || tr.n < 2) { tr.on = false; continue; } if (tr.n > 2 && Math.random() < 0.5) tr.n--; } // shrinks from the tail
      else if (!sim.comets.some((c) => c.alive && c.id === tr.id)) { tr.live = false; tr.t = 0; }
      let n = tr.n; const O = this.ordered;
      for (let i = 0; i < n; i++) { const j = ((tr.head - n + i) % PTS + PTS) % PTS; O[i * 3] = tr.pts[j * 3]; O[i * 3 + 1] = tr.pts[j * 3 + 1]; O[i * 3 + 2] = tr.pts[j * 3 + 2]; }
      if (tr.live && n < PTS + 1) { O[n * 3] = tr.hx; O[n * 3 + 1] = tr.hy; O[n * 3 + 2] = tr.hz; n++; } // up to the head itself
      const fade = tr.live ? 1 : 1 - clamp01(tr.t / LINGER), w = (tr.shard ? 0.5 : tr.evo ? 0.95 : 0.85) * fade;
      this.tails.strip(O, n, 0.06, w, 0.15 * fade, fade, col(_e, TAIL, 1.35), col(_c, 0xc8fff4, 1.6), 0.36, camP, 1.1);
    }
    this.heads.end(); this.halos.end(); this.glints.end(); this.tails.end();
  }

  /** mint light under every comet */
  glowPools(g: GroundGlow, sim: Sim): void {
    for (const c of sim.comets) if (c.alive) g.blob(c.x, this.groundAt(c.x, c.z) + (c.shard ? Y_SHARD : Y), c.z, c.shard ? 1.1 : 1.7, HALO, 0.7);
  }

  clear(): void { this.heads.clear(); this.halos.clear(); this.glints.clear(); this.tails.clear(); for (const t of this.trails) t.on = false; }
}
